import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIButton from 'UIComponents/button/UIButton';
import H1 from 'UIComponents/elements/headings/H1';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UITab from 'UIComponents/nav/UITab';
import UITabs from 'UIComponents/nav/UITabs';
import UISection from 'UIComponents/section/UISection';
import { View } from 'foundations-components/components/View';
import { Section } from 'foundations-components/transitional/components/Section';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScrollEventListener } from 'marketplace-ui-common/hooks/useScrollEventListener';
import { useWindowDimensionsContext } from 'marketplace-ui-common/hooks/useWindowDimensionsContext';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { Fragment, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { getLocationFromQuery } from '../../common/utils/assetQuery';
import { ModuleQuery } from '../../modules/records/ModuleQuery';
import { ThemeQuery } from '../../themes/records/ThemeQuery';
import { getAssetEventSelectorId } from '../../utils/hsAnalytics';
import { getModulesStorefrontRoute, getTemplatesHomePageRoute, getThemesStorefrontRoute } from '../../utils/urls';
import AssetDemoButton from './AssetDemoButton';
import AssetDownloadButton from './AssetDownloadButton';
import AssetSetupGuideButton from './AssetSetupGuideButton';
import AssetShareButton from './AssetShareButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TABS_HEIGHT = 49;
const NoopLink = ({
  children
}) => children;
export default function AssetDetailsHeader({
  assetDetails,
  assetScopes,
  exampleUrl,
  isEmbedded,
  isPreview,
  name,
  offeringType,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  reviewSummary
}) {
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const {
    setupUrl
  } = assetDetails;
  const {
    height: windowHeight
  } = useWindowDimensionsContext();
  const scrollOffset = TABS_HEIGHT + windowHeight / 2;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(['overview', 'features', 'contents', 'support', 'reviews'], scrollOffset);
  useScrollEventListener(calculateActiveTab);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'header'
    }));
  }, [onTrackInteraction]);
  const trackBreadcrumbLinkClick = useCallback(value => {
    trackInteraction('click breadcrumb link', {
      value
    });
  }, [trackInteraction]);
  const trackTabClick = useCallback(value => {
    trackInteraction('click tab navigation', {
      value
    });
  }, [trackInteraction]);
  const providerQuery = useMemo(() => {
    return offeringType === 'MODULE' ? new ModuleQuery({
      providers: [providerName]
    }) : new ThemeQuery({
      providers: [providerName]
    });
  }, [offeringType, providerName]);
  const location = useLocation();
  const providerAssetLocation = useMemo(() => {
    return getLocationFromQuery(providerQuery, location);
  }, [location, providerQuery]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!isEmbedded && /*#__PURE__*/_jsx(UISection, {
      children: offeringType === 'MODULE' ? /*#__PURE__*/_jsxs(UIBreadcrumbs, {
        flush: true,
        children: [/*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('templates storefront home'),
          to: getTemplatesHomePageRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.modules.details.breadcrumbs.root"
          })
        }), /*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('modules'),
          to: getModulesStorefrontRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.modules.details.breadcrumbs.modules"
          })
        }), /*#__PURE__*/_jsx("span", {
          children: name
        })]
      }) : /*#__PURE__*/_jsxs(UIBreadcrumbs, {
        flush: true,
        children: [/*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('templates storefront home'),
          to: getTemplatesHomePageRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.breadcrumbs.root"
          })
        }), /*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('themes'),
          to: getThemesStorefrontRoute('all'),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.breadcrumbs.themes"
          })
        }), /*#__PURE__*/_jsx("span", {
          children: name
        })]
      })
    }), /*#__PURE__*/_jsxs(Section, {
      paddingBlockStart: isEmbedded ? '1000' : undefined,
      marginBlockEnd: isMobileView ? '0' : undefined,
      children: [/*#__PURE__*/_jsx(H1, {
        "aria-level": 1,
        children: name
      }), /*#__PURE__*/_jsxs(UIFlex, {
        align: "baseline",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(UIBadge, {
          use: "koala",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-assets-ui.details.offeringType.${offeringType}`
          })
        }), /*#__PURE__*/_jsx(View, {
          as: "span",
          display: "inline-block",
          marginInline: "300",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.details.header.providerName_jsx",
            options: {
              providerName,
              linkProps: {
                'data-hs-analytics-id': getAssetEventSelectorId('HEADER-PROVIDER-WEBSITE', offeringType),
                to: providerAssetLocation,
                onClick: () => trackInteraction('click provider link')
              }
            },
            elements: {
              Link: isEmbedded ? NoopLink : UIRouterLink
            }
          })
        }), !!(reviewSummary !== null && reviewSummary !== void 0 && reviewSummary.reviewCount) && /*#__PURE__*/_jsx(Rating, {
          iconSize: "300",
          label: /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              scrollToTabSection('reviews');
              trackInteraction('click review count');
            },
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.details.header.reviewCount",
              options: {
                count: reviewSummary.reviewCount
              }
            })
          }),
          rating: reviewSummary.overallRating
        })]
      })]
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: "md",
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(View, {
          as: "div",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(Section, {
            paddingBlockStart: "400",
            children: /*#__PURE__*/_jsxs(UIFlex, {
              children: [/*#__PURE__*/_jsx("div", {
                style: {
                  flexBasis: '50%'
                },
                children: /*#__PURE__*/_jsx(AssetDownloadButton, {
                  marginInlineEnd: isMobileView ? '300' : '0',
                  assetDetails: assetDetails,
                  assetScopes: assetScopes,
                  offeringType: offeringType,
                  section: "header"
                })
              }), /*#__PURE__*/_jsx("div", {
                style: {
                  flexBasis: '50%'
                },
                children: /*#__PURE__*/_jsx(AssetDemoButton, {
                  marginInlineStart: "0",
                  exampleUrl: exampleUrl,
                  modalPrimaryButton: /*#__PURE__*/_jsx(AssetDownloadButton, {
                    assetDetails: assetDetails,
                    assetScopes: assetScopes,
                    offeringType: offeringType
                  }),
                  name: name,
                  onTrackInteraction: onTrackInteraction,
                  onTrackPageview: onTrackPageview,
                  providerName: providerName,
                  section: "header"
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          as: "div",
          textAlign: "center",
          children: /*#__PURE__*/_jsxs(Section, {
            paddingBlock: "0",
            children: [/*#__PURE__*/_jsx(View, {
              as: "span",
              blockSize: "auto",
              marginInlineEnd: "300",
              children: /*#__PURE__*/_jsx(AssetSetupGuideButton, {
                offeringType: offeringType,
                setupUrl: setupUrl
              })
            }), /*#__PURE__*/_jsx(AssetShareButton, {
              section: "header",
              style: {
                width: 'auto'
              },
              use: "link"
            })]
          })
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(UIFlex, {
            align: "center",
            justify: "between",
            children: /*#__PURE__*/_jsx(MediaQueryView, {
              min: "md",
              inlineSize: "100%",
              children: /*#__PURE__*/_jsx(UIGrid, {
                children: /*#__PURE__*/_jsx(UIGridItem, {
                  size: {
                    xs: 12,
                    md: 8
                  },
                  children: /*#__PURE__*/_jsxs(UITabs, {
                    bordered: false,
                    defaultSelected: "overview",
                    onSelectedChange: ({
                      target: {
                        value
                      }
                    }) => {
                      scrollToTabSection(value);
                    },
                    selected: activeTab,
                    children: [/*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('overview'),
                      tabId: "overview",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.overview"
                      })
                    }), /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('features'),
                      tabId: "features",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.features"
                      })
                    }), offeringType === 'THEME' && /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('contents'),
                      tabId: "contents",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.contents"
                      })
                    }), /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('support'),
                      tabId: "support",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.support"
                      })
                    }), /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('reviews'),
                      tabId: "reviews",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.reviews"
                      })
                    })]
                  })
                })
              })
            })
          })
        })
      })
    })]
  });
}